import { useEffect, useState } from 'react';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import logger from '@/utils/logger';
import Seo from '@/components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        // Pocket stones are upsell SKUs shown on PDPs only — exclude from main grid
        const visible = (response.data || []).filter((p) => p.category !== 'pocket-stone');
        setProducts(visible);
        setFilteredProducts(visible);
      } catch (error) {
        logger.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    if (filter === 'all') {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter(p => p.category === filter));
    }
  };

  return (
    <div className="pt-28 pb-16" data-testid="products-page">
      <Seo title="Shop all bracelets" description={"Browse every handmade Morse code bracelet — Two Become One, Soul Sister, Be Still, Akamas Sunset and more. Hand-strung in Paphos, Cyprus."} path="/products" />
      <div className="container-custom px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-caption text-[#84A98C] mb-4">Our Collection</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            Shop All Bracelets
          </h1>
          <p className="text-[#52796F] max-w-lg mx-auto">
            Each piece is handcrafted with care, spelling out meaningful 
            messages in beautiful Morse code beads.
          </p>
        </div>

        {/* Filters */}
        <div className="flex justify-center mb-12">
          <Tabs value={activeFilter} onValueChange={handleFilterChange} className="w-auto">
            <TabsList className="bg-[#F5F5F0] p-1">
              <TabsTrigger 
                value="all" 
                className="data-[state=active]:bg-[#354F52] data-[state=active]:text-[#FAF9F6] text-[#354F52] px-6"
                data-testid="filter-all"
              >
                All
              </TabsTrigger>
              <TabsTrigger 
                value="bestseller"
                className="data-[state=active]:bg-[#354F52] data-[state=active]:text-[#FAF9F6] text-[#354F52] px-6"
                data-testid="filter-bestseller"
              >
                Bestsellers
              </TabsTrigger>
              <TabsTrigger 
                value="collection"
                className="data-[state=active]:bg-[#354F52] data-[state=active]:text-[#FAF9F6] text-[#354F52] px-6"
                data-testid="filter-collection"
              >
                Collection
              </TabsTrigger>
              <TabsTrigger 
                value="custom"
                className="data-[state=active]:bg-[#354F52] data-[state=active]:text-[#FAF9F6] text-[#354F52] px-6"
                data-testid="filter-custom"
              >
                Custom
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-[#CAD2C5] rounded-sm" />
                <div className="mt-4 h-6 bg-[#CAD2C5] rounded w-3/4 mx-auto" />
                <div className="mt-2 h-4 bg-[#CAD2C5] rounded w-1/4 mx-auto" />
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#84A98C]">No products found in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
